import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Typography,
  Button,
  Divider,
  Alert,
  Checkbox,
  FormControlLabel,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { ArrowBack, CalendarToday, Schedule, People, LocationOn, CheckCircle } from '@mui/icons-material';
import { useTranslation, Trans } from 'react-i18next';
import { ThemedPaper } from '@/components/ThemedPaper';
import { ContactForm } from '@/components/ContactForm';
import { LoadingButton } from '@/components/LoadingButton';
import { useReservationStore } from '@/store/reservationStore';
import { useCreateReservation } from '@/hooks/useCreateReservation';
import { useStoreInfo } from '@/hooks/useStoreInfo';
import { createCustomerInfoSchema } from '@/schemas/customerInfo';
import type { CustomerInfoFormData } from '@/schemas/customerInfo';
import { formatTimeSlot } from '@/utils/date';
import { formatPhoneE164 } from '@/utils/phone';
import { getErrorMessage } from '@/utils/error';
import { getWidgetLang, pickLocalizedText } from '@/utils/localizedText';
import { FormattedText } from '@/components/FormattedText';
import { useDomain, useReservePath } from '@/hooks/useDomain';
import {
  isReservationTagName,
  mapReservationTagNamesToUuids,
} from '@/constants/reservationEnums';
import { LanguageEmum } from '@/api/generated/api';
import { trackReservationCompleted } from '@/analytics/ga';

/**
 * Third step: Customer information form and reservation confirmation
 */
export function ConfirmationScreen() {
  const navigate = useNavigate();
  const domain = useDomain();
  const reservePath = useReservePath();
  const { t, i18n } = useTranslation();
  const { date, people, children, selectedTimeslot, reset } = useReservationStore();
  const { data: storeInfo } = useStoreInfo(domain);
  const { mutate: createReservation, isPending, error } = useCreateReservation(domain);

  const widgetLang = getWidgetLang(i18n.language);
  const policyCopy = pickLocalizedText(storeInfo?.widget_settings?.policy_text, widgetLang);
  const policyUrl = storeInfo?.widget_settings?.policy_url?.trim() || null;
  const hasPolicy = !!(policyCopy || policyUrl);

  // Create schema with current language / policy presence - recreate when those change
  const customerInfoSchema = useMemo(
    () => createCustomerInfoSchema({ requirePolicyAcceptance: hasPolicy }),
    [i18n.language, hasPolicy],
  );

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CustomerInfoFormData>({
    resolver: zodResolver(customerInfoSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      specialOccasion: '',
      dietaryRestrictions: [],
      note: '',
      acceptDuration: false,
      acceptTermsAndCancellation: !hasPolicy,
    },
  });

  // Sync acceptance when policy presence changes (e.g. store info loads, language toggles)
  useEffect(() => {
    setValue('acceptTermsAndCancellation', !hasPolicy);
  }, [hasPolicy, setValue]);

  const acceptDuration = watch('acceptDuration', false);
  const acceptTermsAndCancellation = watch('acceptTermsAndCancellation', !hasPolicy);
  const [policyDialogOpen, setPolicyDialogOpen] = useState(false);

  const handleBack = () => {
    navigate(-1);
  };

  const onSubmit = (data: CustomerInfoFormData) => {
    if (!selectedTimeslot) {
      return;
    }

    const selectedTagNames = [
      data.specialOccasion,
      ...(data.dietaryRestrictions || []),
    ].filter((tagName): tagName is string => !!tagName)
      .filter(isReservationTagName);

    const selectedTagUuids = mapReservationTagNamesToUuids(selectedTagNames);

    const notificationLanguage = i18n.language.split('-')[0] === 'el'
      ? LanguageEmum.El
      : LanguageEmum.En;

    // Prepare reservation data for API
    const reservationData = {
      area_uuid: selectedTimeslot.area_uuid || undefined,
      adults: people,
      children: children,
      reservation_starts: selectedTimeslot.from,
      reservation_ends: selectedTimeslot.to,
      first_name: data.firstName,
      last_name: data.lastName,
      phone_number: formatPhoneE164(data.phoneNumber),
      email: data.email,
      tags: selectedTagUuids.length > 0 ? selectedTagUuids : undefined,
      customer_notes: data.note || '',
      notification_language: notificationLanguage,
    };

    createReservation(reservationData, {
      onSuccess: () => {
        trackReservationCompleted();
        // Reset the store and navigate to success page
        reset();
        navigate(`${reservePath}/success`);
      },
    });
  };

  if (!selectedTimeslot || !date) {
    return (
      <ThemedPaper>
        <Box p={4}>
          <Alert severity="warning">
            {t('confirmationScreen.noTimeslotWarning')}
          </Alert>
          <Button onClick={() => navigate(reservePath)} sx={{ mt: 2 }}>
            {t('confirmationScreen.startOver')}
          </Button>
        </Box>
      </ThemedPaper>
    );
  }

  return (
    <ThemedPaper>
      <Box p={4}>
        <Box mb={3}>
          <Button startIcon={<ArrowBack />} onClick={handleBack}>
            {t('common.back')}
          </Button>
        </Box>

        <Typography variant="h5" component="h2" gutterBottom>
          {t('confirmationScreen.title')}
        </Typography>

        {/* Reservation Summary */}
        <Box my={3} p={2} bgcolor="grey.50" borderRadius={1}>
          <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
            {t('confirmationScreen.reservationDetails')}
          </Typography>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <CalendarToday fontSize="small" color="action" />
            <Typography variant="body2">
              <strong>{t('confirmationScreen.date')}:</strong> {date.toLocaleDateString()}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Schedule fontSize="small" color="action" />
            <Typography variant="body2">
              <strong>{t('confirmationScreen.time')}:</strong>{' '}
              {formatTimeSlot(
                selectedTimeslot.from,
                selectedTimeslot.to,
                storeInfo?.show_duration ?? false
              )}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <People fontSize="small" color="action" />
            <Typography variant="body2">
              <strong>{t('confirmationScreen.partySize')}:</strong> {t('partySize.adult', { count: people })}
              {children > 0 && `, ${t('partySize.child', { count: children })}`}
            </Typography>
          </Box>
          {selectedTimeslot.area_name && (
            <Box display="flex" alignItems="center" gap={1}>
              <LocationOn fontSize="small" color="action" />
              <Typography variant="body2">
                <strong>{t('confirmationScreen.area')}:</strong> {selectedTimeslot.area_name}
              </Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          {t('confirmationScreen.yourInformation')}
        </Typography>

        <form onSubmit={handleSubmit(onSubmit)}>
          <ContactForm control={control} errors={errors} />

          {error && (
            <Alert severity="error" sx={{ mt: 3 }}>
              <FormattedText text={getErrorMessage(error, storeInfo?.widget_settings)} />
            </Alert>
          )}

          {/* Reservation policy: text opens popup, URL opens new tab; hide if neither */}
          {hasPolicy && (
            <FormControlLabel
              control={
                <Controller
                  name="acceptTermsAndCancellation"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      checked={field.value}
                      color="primary"
                    />
                  )}
                />
              }
              label={
                <Typography component="span" variant="body1">
                  <Trans
                    i18nKey="confirmationScreen.acceptTermsAndCancellation"
                    components={{
                      policyLink: policyCopy ? (
                        <Link
                          component="button"
                          type="button"
                          underline="hover"
                          color="primary"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setPolicyDialogOpen(true);
                          }}
                          sx={{
                            display: 'inline',
                            verticalAlign: 'baseline',
                            font: 'inherit',
                          }}
                        />
                      ) : (
                        <Link
                          href={policyUrl!}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          color="primary"
                          onClick={(e) => e.stopPropagation()}
                        />
                      ),
                    }}
                  />
                </Typography>
              }
              sx={{ mt: 2 }}
            />
          )}

          <Dialog
            open={policyDialogOpen}
            onClose={() => setPolicyDialogOpen(false)}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>{t('confirmationScreen.reservationPolicyTitle')}</DialogTitle>
            <DialogContent dividers>
              {policyCopy && (
                <FormattedText text={policyCopy} variant="body2" color="text.secondary" />
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPolicyDialogOpen(false)}>
                {t('common.close')}
              </Button>
            </DialogActions>
          </Dialog>

          {/* Conditional: Duration (existing) */}
          {storeInfo?.show_duration && (
            <FormControlLabel
              control={
                <Controller
                  name="acceptDuration"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      {...field}
                      checked={field.value}
                      color="primary"
                    />
                  )}
                />
              }
              label={t('confirmationScreen.acceptDuration')}
              sx={{ mt: 2 }}
            />
          )}


          {/* Agreement Note with Links */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
            <Trans
              i18nKey="confirmationScreen.agreementNote"
              components={{
                termsLink: (
                  <Link
                    href="https://sala.gr/terms-of-use"
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    color="primary"
                  />
                ),
                privacyLink: (
                  <Link
                    href="https://sala.gr/privacy-policy"
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    color="primary"
                  />
                ),
              }}
            />
          </Typography>

          <Box mt={4}>
            <LoadingButton
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              loading={isPending}
              disabled={
                (hasPolicy && !acceptTermsAndCancellation) ||
                (storeInfo?.show_duration ? !acceptDuration : false)
              }
              endIcon={!isPending ? <CheckCircle /> : undefined}
            >
              {isPending ? t('confirmationScreen.submitting') : t('confirmationScreen.submitReservation')}
            </LoadingButton>
          </Box>
        </form>
      </Box>
    </ThemedPaper>
  );
}
